import type { UrlSegment } from '@angular/router';

// Global params that are banned on all routes
const GLOBALLY_BANNED_PARAMS = ['hlText', 'page'];

// Global param+value combinations that are banned on all routes
const GLOBALLY_BANNED_PARAM_VALUES: { key: string; value: string }[] = [{ key: 'utm_source', value: 'rei' }];

// Blog-specific params that only work within blog routes
const BLOG_ONLY_PARAMS = ['cat', 'state'];

// Preview-specific params that only work on /preview route
const PREVIEW_ONLY_PARAMS = ['slug', 'token'];

// Link-owned params: a link keeps the value it declares, but never inherits one from the current URL.
// `a` selects a Gladly support article; only a link that names an article may carry one.
const LINK_OWNED_PARAMS = ['a'];

/**
 * Checks if a link is a same-page anchor (e.g. `#faq`), which stays on the current URL
 * @param url - URL to check
 * @returns true if the link is hash-only
 */
function isSamePageAnchor(url: string): boolean {
  return !!url && url.startsWith('#');
}

/**
 * Checks if a URL path targets the preview route
 * @param url - URL to check (relative, absolute, with query params/anchors)
 * @returns true if URL targets /preview route
 */
function isPreviewRoute(url: string): boolean {
  if (!url) return false;

  let cleanPath = url;

  // Handle absolute URLs (extract pathname)
  if (url.includes('://')) {
    try {
      cleanPath = new URL(url).pathname;
    } catch {
      // Fallback for malformed URLs
      const pathPart = url.split('://')[1]?.split('/').slice(1).join('/');
      cleanPath = pathPart ? '/' + pathPart : url;
    }
  }

  // Strip query params and anchors to get clean path
  cleanPath = cleanPath.split('?')[0].split('#')[0];

  // Remove trailing slash for comparison
  cleanPath = cleanPath.replace(/\/$/, '');

  return cleanPath === '/preview' || cleanPath === 'preview';
}

/**
 * Checks if a URL path targets a blog route
 * @param url - URL to check (relative, absolute, with query params/anchors)
 * @param blogPrefix - Environment blogPrefix (e.g., 'blog--', 'defensive-driving--trending--')
 * @returns true if URL targets a blog route
 */
function isBlogRoute(url: string, blogPrefix: string): boolean {
  if (!blogPrefix || !url) return false;

  // Convert blogPrefix to URL path (blog-- → /blog)
  const blogPath = '/' + blogPrefix.replace(/--+$/, '').replace(/--/g, '/');

  let cleanPath = url;

  // Handle absolute URLs (extract pathname)
  if (url.includes('://')) {
    try {
      cleanPath = new URL(url).pathname;
    } catch {
      // Fallback for malformed URLs
      const pathPart = url.split('://')[1]?.split('/').slice(1).join('/');
      cleanPath = pathPart ? '/' + pathPart : url;
    }
  }

  // Strip query params and anchors to get clean path
  cleanPath = cleanPath.split('?')[0].split('#')[0];

  return cleanPath.startsWith(blogPath);
}

/**
 * Checks if a URL is a blog homepage (listing page) vs a blog entry (individual post)
 * @param url - URL to check
 * @param blogPrefix - Environment blogPrefix
 * @returns true if URL is blog homepage, false if blog entry or non-blog
 */
function isBlogHomepage(url: string, blogPrefix: string): boolean {
  if (!isBlogRoute(url, blogPrefix)) return false;

  // Convert blogPrefix to URL path
  const blogPath = '/' + blogPrefix.replace(/--+$/, '').replace(/--/g, '/');

  let cleanPath = url;

  // Handle absolute URLs
  if (url.includes('://')) {
    try {
      cleanPath = new URL(url).pathname;
    } catch {
      const pathPart = url.split('://')[1]?.split('/').slice(1).join('/');
      cleanPath = pathPart ? '/' + pathPart : url;
    }
  }

  // Strip query params and anchors
  cleanPath = cleanPath.split('?')[0].split('#')[0];

  // Remove trailing slash for comparison
  cleanPath = cleanPath.replace(/\/$/, '');
  const normalizedBlogPath = blogPath.replace(/\/$/, '');

  // If path equals blog path exactly, it's the homepage
  // Examples: /blog = homepage, /blog/post-title = entry
  return cleanPath === normalizedBlogPath;
}

function isBannedParamValue(key: string, value: string): boolean {
  return GLOBALLY_BANNED_PARAM_VALUES.some((banned) => banned.key === key && banned.value === value);
}

function sanitizeQueryParamValue(value: string): string | null {
  // regex should match alphanumeric characters, dashes, underscores, exclamation marks, periods, colons, slashes, equals, plus signs and %, but % should not be followed by any other characters.
  // Added support for common URL parameter characters including those used in tracking parameters like mbsy_exp
  // Added support for common URL parameter characters including those like: mbsy_exp=Thu,%2011%20Sep%202025%2016:35:57%20GMT
  // Values are not encoded and the output is spliced into trusted, quoted HTML attributes, so never allow " ' < > &.
  const charWhitelist = /^[a-zA-Z0-9-_!.:/=+ ,]*(%[a-fA-F0-9]{2}|%[a-zA-Z0-9-_!.:/=+ ,]*)*$/;

  if (!value || charWhitelist.test(value) === false) {
    return null;
  }
  return value;
}

/**
 * Percent-encodes a query param name once. Output may be spliced into raw HTML attributes;
 * encodeURIComponent already covers a double-quoted href, and !'()* are encoded too so a name is
 * also safe in a single-quoted one. Values may contain spaces, so the full query string must stay
 * inside a quoted attribute.
 */
function encodeQueryParamName(name: string): string {
  return encodeURIComponent(name).replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
}

/**
 * @param queryParamObject - Decoded names and values, as produced by ActivatedRoute. Names are
 * percent-encoded here exactly once; values pass the whitelist unencoded.
 */
export function getQueryParamString(queryParamObject: QueryParam, targetUrl?: string, blogPrefix?: string): string {
  if (!queryParamObject) {
    return '';
  }

  const isBlogHomepageDestination = targetUrl && blogPrefix ? isBlogHomepage(targetUrl, blogPrefix) : false;
  const isPreviewDestination = targetUrl ? isPreviewRoute(targetUrl) : false;
  const isSamePageDestination = isSamePageAnchor(targetUrl);

  const validParams: { [key: string]: string } = {};
  Object.keys(queryParamObject).forEach((key) => {
    // Skip globally banned params
    if (GLOBALLY_BANNED_PARAMS.includes(key)) return;

    // Skip globally banned param+value combinations
    if (isBannedParamValue(key, queryParamObject[key])) return;

    // Skip blog-only params when destination is NOT blog homepage
    if (!isBlogHomepageDestination && BLOG_ONLY_PARAMS.includes(key)) return;

    // Skip preview-only params when destination is NOT preview route
    if (!isPreviewDestination && PREVIEW_ONLY_PARAMS.includes(key)) return;

    // Skip link-owned params unless the link stays on the current page
    if (!isSamePageDestination && LINK_OWNED_PARAMS.includes(key)) return;

    if (queryParamObject[key]) {
      const sanitizedValue = sanitizeQueryParamValue(queryParamObject[key]);
      if (sanitizedValue !== null) {
        validParams[key] = sanitizedValue;
      }
    }
  });

  return Object.entries(validParams)
    .map(([key, value]) => `${encodeQueryParamName(key)}=${value}`)
    .join('&');
}

/**
 * Removes empty and repeated values from a comma-separated list, keeping the first occurrence.
 * Values are compared trimmed but keep their original text, so comma-containing scalars
 * (e.g. mbsy_exp dates) pass through unchanged.
 * @param list - Comma-separated value
 * @returns The deduplicated list
 */
function dedupeCommaList(list: string): string {
  const seen = new Set<string>();
  return list
    .split(',')
    .filter((token) => {
      const value = token.trim();
      if (!value || seen.has(value)) return false;
      seen.add(value);
      return true;
    })
    .join(',');
}

function recodeCommas(paramString: URLSearchParams): string {
  return paramString.toString().replace(/%2C/g, ',');
}

export function mergeSearchParams(link: string, paramString: string, blogPrefix?: string): string {
  const urlSegments = link.split('?');
  const queryParams = new URLSearchParams(urlSegments[1]);
  const newQueryParams = new URLSearchParams(paramString);

  const isBlogHomepageDestination = blogPrefix ? isBlogHomepage(link, blogPrefix) : false;
  const isPreviewDestination = isPreviewRoute(link);

  // Sanitize and merge parameters
  newQueryParams.forEach((value, key) => {
    // Skip globally banned
    if (GLOBALLY_BANNED_PARAMS.includes(key)) return;

    // Skip globally banned param+value combinations
    if (isBannedParamValue(key, value)) return;

    // Skip blog-only params when destination is NOT blog homepage
    if (!isBlogHomepageDestination && BLOG_ONLY_PARAMS.includes(key)) return;

    // Skip preview-only params when destination is NOT preview route
    if (!isPreviewDestination && PREVIEW_ONLY_PARAMS.includes(key)) return;

    // Skip link-owned params; the link's own value (if any) is already in queryParams
    if (LINK_OWNED_PARAMS.includes(key)) return;

    const sanitizedValue = sanitizeQueryParamValue(value);
    if (sanitizedValue !== null) {
      // For blog-only params: if link already has them, prefer link's value (don't merge)
      // This prevents accumulation when clicking category/state filters
      if (BLOG_ONLY_PARAMS.includes(key) && queryParams.has(key)) {
        // Link already has this param - skip merging, use link's value
        return;
      }

      if (queryParams.has(key)) {
        const existingValue = queryParams.get(key);
        const sanitizedExisting = sanitizeQueryParamValue(existingValue);
        if (sanitizedExisting !== null) {
          queryParams.set(key, dedupeCommaList(`${sanitizedExisting},${sanitizedValue}`));
        }
      } else {
        queryParams.set(key, sanitizedValue);
      }
    }
  });
  return recodeCommas(queryParams);
}

export function getParsedLinkString(
  link: string,
  paramString: string,
  hostName: string,
  siblingSites: string[],
  blogPrefix?: string,
): string {
  //step 0, return null links
  if (link === null) {
    return link;
  }
  //first, return any links that won't be parsed; external links, file links
  if (isFileLink(link) || (!isRelativeLink(link) && !isQueryParamFullLink(link, hostName, siblingSites))) {
    return link;
  }
  // second, remove duplicate query params from incoming paramString
  const passedParams = new URLSearchParams(paramString);
  const thinnedParams = new URLSearchParams();
  const isBlogHomepageDestination = blogPrefix ? isBlogHomepage(link, blogPrefix) : false;
  const isPreviewDestination = isPreviewRoute(link);
  const isSamePageDestination = isSamePageAnchor(link);

  passedParams.forEach((value, key) => {
    // Skip globally banned
    if (GLOBALLY_BANNED_PARAMS.includes(key)) return;

    // Skip globally banned param+value combinations
    if (isBannedParamValue(key, value)) return;

    // Skip blog-only params when destination is NOT blog homepage
    if (!isBlogHomepageDestination && BLOG_ONLY_PARAMS.includes(key)) return;

    // Skip preview-only params when destination is NOT preview route
    if (!isPreviewDestination && PREVIEW_ONLY_PARAMS.includes(key)) return;

    // Skip link-owned params unless the link stays on the current page
    if (!isSamePageDestination && LINK_OWNED_PARAMS.includes(key)) return;

    value = dedupeCommaList(value);
    if ((thinnedParams.has(key) && thinnedParams.get(key) !== value) || !thinnedParams.has(key)) {
      thinnedParams.set(key, value);
    }
  });
  paramString = recodeCommas(thinnedParams);

  // third, merge new query params with existing query params. Split the fragment off first so it
  // isn't parsed into the last value, and replace the link's query: the merged string already holds it.
  const hashIndex = link.indexOf('#');
  const linkWithoutHash = hashIndex === -1 ? link : link.slice(0, hashIndex);
  if (linkWithoutHash.includes('?')) {
    paramString = mergeSearchParams(linkWithoutHash, paramString, blogPrefix);
    return `${linkWithoutHash.split('?')[0]}?${paramString}${hashIndex === -1 ? '' : link.slice(hashIndex)}`;
  }
  //fourth, perform special link adjustments for anchors.
  if (hashIndex !== -1) {
    return addQueryParamstoAnchor(paramString, link);
  }
  //Everything else gets the standard queryparam tack-on, as long as there's a queryParam.
  if (paramString.length > 0) {
    return `${link}?${paramString}`;
  }
  return link;
}

export function addQueryParamstoAnchor(queryParamString: string, link: string): string {
  if (queryParamString.length === 0) {
    return link;
  }
  const hashIndex = link.indexOf('#');
  // Handle hash-only links (e.g., "#section") — preserve current page context
  if (hashIndex === 0) {
    return '?' + queryParamString + link;
  }
  const base = link.slice(0, hashIndex);
  const hash = link.slice(hashIndex);
  if (base.includes('?')) {
    return `${base}&${queryParamString}${hash}`;
  }
  // Same rule as WysiwygRedactorService.addTrailingSlashToHref: a bare host gets a slash, but a
  // file/asset URL does not, since /doc.pdf/ breaks the download.
  const authority = base.match(/^(?:[a-zA-Z][a-zA-Z0-9+.-]*:)?\/\//);
  const isBareHost = !!authority && base.indexOf('/', authority[0].length) === -1;
  const isFile = !isBareHost && /\.[a-z0-9]+$/i.test(base.slice(base.lastIndexOf('/') + 1));
  return `${base.endsWith('/') || isFile ? base : `${base}/`}?${queryParamString}${hash}`;
}

export function testPathList(url: string, currentPathList: UrlSegment[]): boolean {
  for (const linkSegment of currentPathList) {
    if (linkSegment.path === url) {
      return true;
    }
  }
  return false;
}

export function isQueryParamFullLink(link: string, hostName: string, siblingSites: string[]): boolean {
  const isSiblingSite = siblingSites.some((site) => link.includes(site));
  return isSiblingSite || link.includes(hostName);
}

export function isRelativeLink(link: string): boolean {
  const regexp = /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!/-]))?/;
  return !regexp.test(link);
}

export function isFileLink(link: string): boolean {
  const regexp = /\.(txt|pdf|doc|rtf|xml)/;
  return regexp.test(link);
}

export function ensureTrailingSlash(link: string): string;
export function ensureTrailingSlash(link: null): null;
export function ensureTrailingSlash(link: undefined): undefined;
export function ensureTrailingSlash(link: string | null | undefined): string | null | undefined;
export function ensureTrailingSlash(link: string | null | undefined): string | null | undefined {
  if (!link) return link;
  // Skip URI-scheme links (mailto:, tel:, javascript:, etc.) and protocol-relative URLs (//example.com)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(link) || link.startsWith('//')) return link;
  if (!isRelativeLink(link)) return link;
  if (isFileLink(link)) return link;
  if (link.includes('?') || link.includes('#')) return link;
  return link.endsWith('/') ? link : `${link}/`;
}

export interface QueryParam {
  [key: string]: string;
}
